/**
 * Classe base per tutti i filtri del sistema FilterHandler
 *
 * @description
 * BaseFilter fornisce le funzionalità comuni a tutti i tipi di filtri:
 * - Gestione del valore e valore di default
 * - Serializzazione/deserializzazione da/verso query string
 * - Costruzione di query OData
 * - Operatori di ricerca (equals, contains, greaterThan, ecc.)
 *
 * Questa classe è astratta e deve essere estesa da filtri specifici.
 */

import type {
	FilterType,
	FilterValue,
	QueryParams,
	ODataContext,
	FilterDefinition,
	SearchOperatorType,
} from '../types';
import { SearchOperator } from '../types';

/**
 * Classe base astratta per tutti i filtri
 *
 * @example
 * // Non può essere istanziata direttamente
 * // const filter = new BaseFilter(); // ❌ Errore!
 *
 * // Deve essere estesa da filtri specifici
 * class CustomFilter extends BaseFilter<string> {
 *   constructor(propertyName: string) {
 *     super(String, propertyName);
 *   }
 * }
 *
 * @template T - Tipo del valore del filtro
 * @abstract
 * @category Core
 * @since 1.0.0
 */
export abstract class BaseFilter<T extends FilterValue = FilterValue> {
	/**
	 * Tipo del filtro (String, Number, Boolean, Date, Array, Entity)
	 * @protected
	 */
	protected _type: FilterType;

	/**
	 * Valore corrente del filtro
	 * @public
	 */
	public value: T = null as T;

	/**
	 * Valore di default del filtro
	 * @protected
	 */
	protected _defaultValue: T = null as T;

	/**
	 * Se true, aggiorna la query string quando il filtro cambia
	 * @protected
	 */
	protected _updateQueryOnChange: boolean = false;

	/**
	 * Nome della proprietà su cui applicare il filtro
	 * @protected
	 */
	protected _propertyName: string = '';

	/**
	 * Nome della proprietà nella query string (sanitizzato)
	 * @protected
	 */
	protected _queryPropertyName: string = '';

	/**
	 * Descrizione human-readable del filtro (opzionale)
	 * @protected
	 */
	protected _description: string = '';

	/**
	 * Operatore di ricerca da applicare
	 * @protected
	 */
	protected _searchOperator: SearchOperatorType = SearchOperator.NOOP;

	/**
	 * Nome della proprietà secondaria per operatori complessi (ANY, ALL)
	 * @protected
	 */
	protected _subPropertyName: string = '';

	/**
	 * Operatore di ricerca per la proprietà secondaria
	 * @protected
	 */
	protected _subSearchOperator: SearchOperatorType = SearchOperator.NOOP;

	/**
	 * Nomi delle proprietà nidificate per operatori complessi
	 * @protected
	 */
	protected _nestedPropertyNames: string[] | null = null;

	/**
	 * Crea una nuova istanza di BaseFilter
	 *
	 * @param type - Tipo del filtro
	 * @param propertyName - Nome della proprietà da filtrare
	 * @param value - Valore iniziale (opzionale)
	 * @param defaultValue - Valore di default (opzionale)
	 * @param updateQueryOnChange - Se aggiornare la query string al cambio (opzionale)
	 *
	 * @throws {TypeError} Se propertyName non è una stringa valida
	 *
	 * @protected
	 */
	protected constructor(
		type: FilterType,
		propertyName: string,
		value: T = null as T,
		defaultValue: T = null as T,
		updateQueryOnChange: boolean = false,
	) {
		this._type = type;

		if (!propertyName || typeof propertyName !== 'string' || propertyName === '') {
			throw new TypeError('PropertyName must be a non empty string!');
		}

		this._propertyName = propertyName.replace(/\./g, '/');
		this._queryPropertyName = this._sanitize(propertyName);
		this.value = value;
		this._defaultValue = defaultValue;
		this._updateQueryOnChange = updateQueryOnChange;
	}

	/**
	 * Sanitizza un nome di proprietà per l'uso nelle query string
	 * Sostituisce i punti con underscore per evitare problemi nell'URL
	 *
	 * @param value - Valore da sanitizzare
	 * @returns Valore sanitizzato
	 *
	 * @example
	 * this._sanitize('order.customer.name') // 'order_customer_name'
	 *
	 * @protected
	 */
	protected _sanitize(value: string): string {
		return value.replace(/\./g, '_');
	}

	/**
	 * Legge il valore del filtro dalla query string
	 *
	 * @param query - Parametri della query string
	 *
	 * @example
	 * const query = { customer_name: 'Acme Corp' };
	 * filter._fromQuery(query); // Imposta il valore del filtro a 'Acme Corp'
	 *
	 * @protected
	 */
	protected _fromQuery(query: QueryParams): void {
		const value = query[this._queryPropertyName];
		if (value !== undefined && value !== this.value) {
			this.value = value as T;
		}
	}

	/**
	 * Scrive il valore del filtro nella query string
	 * Se il valore è quello di default, rimuove la proprietà dalla query
	 *
	 * @param query - Oggetto query string da modificare
	 *
	 * @example
	 * const query = {};
	 * filter.value = 'test';
	 * filter._toQuery(query); // query diventa { customer_name: 'test' }
	 *
	 * @protected
	 */
	protected _toQuery(query: QueryParams): void {
		// FIX: Serializza null esplicitamente se diverso dal default
		// Questo permette di distinguere "valore nullo intenzionale" da "valore di default"
		if (this.value === null && this._defaultValue !== null) {
			query[this._queryPropertyName] = 'null';
			return;
		}

		if (this._valueIsDefault()) {
			delete query[this._queryPropertyName];
		} else {
			const serialized = this._getSerializedValue();
			if (serialized !== undefined) {
				query[this._queryPropertyName] = serialized;
			} else {
				delete query[this._queryPropertyName];
			}
		}
	}

	/**
	 * Restituisce il valore serializzato per la query string
	 * Di default restituisce il valore così com'è, ma può essere sovrascritto
	 * dai filtri derivati per personalizzare la serializzazione
	 *
	 * @returns Valore serializzato
	 *
	 * @example
	 * // In DateFilter
	 * _getSerializedValue() {
	 *   return this.value ? this.value.toISOString() : null;
	 * }
	 *
	 * @protected
	 */
	protected _getSerializedValue(): string | number | undefined {
		return this.value as string | number | undefined;
	}

	/**
	 * Verifica se il valore corrente è quello di default
	 *
	 * @returns true se il valore è di default, false altrimenti
	 *
	 * @example
	 * filter.value = null;
	 * filter._defaultValue = null;
	 * filter._valueIsDefault(); // true
	 *
	 * filter.value = 'test';
	 * filter._valueIsDefault(); // false
	 *
	 * @protected
	 */
	protected _valueIsDefault(): boolean {
		return this.value === undefined || this.value === this._defaultValue;
	}

	// =================
	// METODI PUBBLICI - CONFIGURAZIONE
	// =================

	/**
	 * Imposta il valore iniziale del filtro
	 *
	 * @param value - Valore iniziale da impostare
	 * @returns L'istanza del filtro (per method chaining)
	 *
	 * @example
	 * const filter = new StringFilter('name')
	 *   .initialValue('Mario Rossi')
	 *   .contains();
	 *
	 * @public
	 */
	public initialValue(value: T): this {
		this.value = value;
		return this;
	}

	/**
	 * Imposta il valore di default del filtro
	 * Se il valore del filtro è uguale al default, non verrà incluso nella query string
	 *
	 * @param value - Valore di default da impostare
	 * @returns L'istanza del filtro (per method chaining)
	 *
	 * @example
	 * const filter = new BooleanFilter('active')
	 *   .default(true)  // Se il valore è true, non appare nell'URL
	 *   .equals();
	 *
	 * @public
	 */
	public default(value: T): this {
		this._defaultValue = value;
		return this;
	}

	/**
	 * Configura se aggiornare la query string quando il filtro cambia
	 *
	 * @param value - true per aggiornare la query string, false altrimenti
	 * @returns L'istanza del filtro (per method chaining)
	 *
	 * @throws {TypeError} Se il parametro non è un boolean
	 *
	 * @example
	 * const filter = new StringFilter('search')
	 *   .updateQueryOnFilterChange(true)  // Aggiorna l'URL ad ogni cambio
	 *   .contains();
	 *
	 * @public
	 */
	public updateQueryOnFilterChange(value: boolean): this {
		if (typeof value === 'boolean') {
			this._updateQueryOnChange = value;
			return this;
		}
		throw new TypeError('Parameter is not a boolean!');
	}

	/**
	 * Imposta il nome della proprietà per le query dati e la query string
	 *
	 * @param value - Nome della proprietà
	 * @returns L'istanza del filtro (per method chaining)
	 *
	 * @throws {TypeError} Se il parametro non è una stringa valida
	 *
	 * @example
	 * const filter = new StringFilter('name')
	 *   .propertyName('customer.companyName')  // Filtra su customer.companyName
	 *   .contains();
	 *
	 * @public
	 */
	public propertyName(value: string): this {
		if (value && typeof value === 'string' && value !== '') {
			this._propertyName = value.replace(/\./g, '/');
			this._queryPropertyName = this._sanitize(value);
			return this;
		}
		throw new TypeError('Parameter is not a valid string!');
	}

	/**
	 * Imposta il nome della proprietà nella query string (sovrascrive quello auto-generato)
	 *
	 * @param value - Nome della proprietà nella query string
	 * @returns L'istanza del filtro (per method chaining)
	 *
	 * @throws {TypeError} Se il parametro non è una stringa valida
	 *
	 * @example
	 * const filter = new StringFilter('customer.name')
	 *   .queryPropertyName('customerName')  // Nell'URL apparirà ?customerName=...
	 *   .contains();
	 *
	 * @public
	 */
	public queryPropertyName(value: string): this {
		if (value && typeof value === 'string' && value !== '') {
			this._queryPropertyName = this._sanitize(value);
			return this;
		}
		throw new TypeError('Parameter is not a valid string!');
	}

	// =================
	// OPERATORI DI RICERCA
	// =================

	/**
	 * Imposta l'operatore 'uguale' per le query dati
	 *
	 * @returns L'istanza del filtro (per method chaining)
	 *
	 * @example
	 * const filter = new NumberFilter('customerId')
	 *   .equals();  // WHERE customerId = value
	 *
	 * @public
	 */
	public equals(): this {
		return this._filter(SearchOperator.EQUAL);
	}

	/**
	 * Imposta l'operatore 'diverso da' per le query dati
	 *
	 * @returns L'istanza del filtro (per method chaining)
	 *
	 * @example
	 * const filter = new StringFilter('status')
	 *   .notEquals();  // WHERE status != value
	 *
	 * @public
	 */
	public notEquals(): this {
		return this._filter(SearchOperator.NOTEQUAL);
	}

	/**
	 * Imposta l'operatore 'contiene' per le query dati
	 * Funziona principalmente con stringhe
	 *
	 * @returns L'istanza del filtro (per method chaining)
	 *
	 * @example
	 * const filter = new StringFilter('description')
	 *   .contains();  // WHERE tolower(description) contains 'value'
	 *
	 * @public
	 */
	public contains(): this {
		return this._filter(SearchOperator.CONTAINS);
	}

	/**
	 * Imposta l'operatore 'inizia con' per le query dati
	 * Funziona principalmente con stringhe
	 *
	 * @returns L'istanza del filtro (per method chaining)
	 *
	 * @example
	 * const filter = new StringFilter('code')
	 *   .startsWith();  // WHERE tolower(code) startswith 'value'
	 *
	 * @public
	 */
	public startsWith(): this {
		return this._filter(SearchOperator.STARTSWITH);
	}

	/**
	 * Imposta l'operatore 'termina con' per le query dati
	 * Funziona principalmente con stringhe
	 *
	 * @returns L'istanza del filtro (per method chaining)
	 *
	 * @example
	 * const filter = new StringFilter('email')
	 *   .endsWith();  // WHERE tolower(email) endswith 'value'
	 *
	 * @public
	 */
	public endsWith(): this {
		return this._filter(SearchOperator.ENDSWITH);
	}

	/**
	 * Imposta l'operatore 'maggiore di' per le query dati
	 * Funziona con numeri e date
	 *
	 * @returns L'istanza del filtro (per method chaining)
	 *
	 * @example
	 * const filter = new NumberFilter('price')
	 *   .greaterThan();  // WHERE price > value
	 *
	 * @public
	 */
	public greaterThan(): this {
		return this._filter(SearchOperator.GREATERTHAN);
	}

	/**
	 * Imposta l'operatore 'maggiore o uguale a' per le query dati
	 * Funziona con numeri e date
	 *
	 * @returns L'istanza del filtro (per method chaining)
	 *
	 * @example
	 * const filter = new DateFilter('startDate')
	 *   .greaterThanOrEqual();  // WHERE startDate >= value
	 *
	 * @public
	 */
	public greaterThanOrEqual(): this {
		return this._filter(SearchOperator.GREATEREQUAL);
	}

	/**
	 * Imposta l'operatore 'minore di' per le query dati
	 * Funziona con numeri e date
	 *
	 * @returns L'istanza del filtro (per method chaining)
	 *
	 * @example
	 * const filter = new NumberFilter('quantity')
	 *   .lesserThan();  // WHERE quantity < value
	 *
	 * @public
	 */
	public lesserThan(): this {
		return this._filter(SearchOperator.LESSERTHAN);
	}

	/**
	 * Imposta l'operatore 'minore o uguale a' per le query dati
	 * Funziona con numeri e date
	 *
	 * @returns L'istanza del filtro (per method chaining)
	 *
	 * @example
	 * const filter = new DateFilter('endDate')
	 *   .lesserThanOrEqual();  // WHERE endDate <= value
	 *
	 * @public
	 */
	public lesserThanOrEqual(): this {
		return this._filter(SearchOperator.LESSEREQUAL);
	}

	/**
	 * Imposta l'operatore 'in' per le query dati
	 * Il valore deve essere contenuto nell'array specificato
	 *
	 * @returns L'istanza del filtro (per method chaining)
	 *
	 * @example
	 * const filter = new ArrayFilter('status')
	 *   .in();  // WHERE status in ('active', 'pending')
	 *
	 * @public
	 */
	public in(): this {
		return this._filter(SearchOperator.IN);
	}

	/**
	 * Imposta l'operatore 'non compreso tra' per le query dati
	 * Il valore deve essere al di fuori del range specificato
	 *
	 * @returns L'istanza del filtro (per method chaining)
	 *
	 * @example
	 * const filter = new NumberRangeFilter('price')
	 *   .notBetween();  // WHERE price < min OR price > max
	 *
	 * @public
	 */
	public notBetween(): this {
		return this._filter(SearchOperator.NOTBETWEEN);
	}

	/**
	 * Imposta l'operatore 'compreso tra' per le query dati
	 * Il valore deve essere all'interno del range specificato
	 *
	 * @returns L'istanza del filtro (per method chaining)
	 *
	 * @example
	 * const filter = new DateRangeFilter('createdAt')
	 *   .between();  // WHERE createdAt >= startDate AND createdAt <= endDate
	 *
	 * @public
	 */
	public between(): this {
		return this._filter(SearchOperator.BETWEEN);
	}

	/**
	 * Imposta l'operatore 'any' per query su collezioni
	 * Almeno un elemento della collezione deve soddisfare la condizione
	 *
	 * @param subPropertyName - Nome della proprietà nella relazione da filtrare
	 * @param subSearchOperator - Operatore da applicare alla proprietà secondaria
	 * @param nested - Array di proprietà nidificate (opzionale)
	 * @returns L'istanza del filtro (per method chaining)
	 *
	 * @throws {TypeError} Se i parametri non sono validi
	 *
	 * @example
	 * const filter = new NumberFilter('orders')
	 *   .any('customerId', SearchOperator.EQUAL);  // WHERE orders/any(o: o/customerId eq value)
	 *
	 * @public
	 */
	public any(
		subPropertyName: string,
		subSearchOperator: SearchOperatorType,
		nested: string[] = [],
	): this {
		if (subPropertyName && typeof subPropertyName === 'string' && subPropertyName !== '') {
			this._subPropertyName = subPropertyName;
		}

		if (subSearchOperator && typeof subSearchOperator === 'symbol') {
			this._subSearchOperator = subSearchOperator;
		}

		if (Array.isArray(nested)) {
			this._nestedPropertyNames = nested.map((item) => item.replace(/\./g, '/'));
		}

		if (
			this._subPropertyName === '' ||
			this._subSearchOperator === SearchOperator.NOOP ||
			this._nestedPropertyNames === null
		) {
			throw new TypeError('Supplied parameters are not valid!');
		}

		return this._filter(SearchOperator.ANY);
	}

	/**
	 * Imposta l'operatore 'all' per query su collezioni
	 * Tutti gli elementi della collezione devono soddisfare la condizione
	 *
	 * @returns L'istanza del filtro (per method chaining)
	 *
	 * @example
	 * const filter = new NumberFilter('orders')
	 *   .all();  // WHERE orders/all(o: o/condition)
	 *
	 * @public
	 */
	public all(): this {
		return this._filter(SearchOperator.ALL);
	}

	/**
	 * Imposta internamente l'operatore di ricerca
	 *
	 * @param operator - Operatore da impostare
	 * @returns L'istanza del filtro (per method chaining)
	 *
	 * @throws {TypeError} Se l'operatore non è valido
	 *
	 * @protected
	 */
	protected _filter(operator: SearchOperatorType): this {
		if (operator && typeof operator === 'symbol') {
			this._searchOperator = operator;
			return this;
		}
		throw new TypeError('Parameter is not a valid SearchOperator!');
	}

	// =================
	// DESCRIZIONE FILTRO
	// =================

	/**
	 * Restituisce la descrizione human-readable del filtro
	 * Se non impostata esplicitamente, estrae l'ultima parte del property name (dopo il punto)
	 *
	 * @returns Descrizione del filtro
	 *
	 * @example
	 * // Con descrizione esplicita
	 * const filter = new NumberFilter('workerId').description('Collaboratore');
	 * filter.filterDescription; // 'Collaboratore'
	 *
	 * // Fallback automatico (proprietà semplice)
	 * const filter = new NumberFilter('workerId');
	 * filter.filterDescription; // 'workerId'
	 *
	 * // Fallback automatico (proprietà nidificata)
	 * const filter = new NumberFilter('order.customerId');
	 * filter.filterDescription; // 'customerId'
	 *
	 * @public
	 */
	public get filterDescription(): string {
		if (this._description) {
			return this._description;
		}
		// Fallback: estrai l'ultima parte del property name
		// 'order.customerId' → 'customerId'
		// 'workerId' → 'workerId'
		const parts = this._propertyName.replace(/\//g, '.').split('.');
		return parts[parts.length - 1];
	}

	/**
	 * Imposta una descrizione human-readable per il filtro
	 * Utilizzata per visualizzare il filtro attivo nell'UI
	 *
	 * @param desc - Descrizione del filtro
	 * @returns L'istanza del filtro (per method chaining)
	 *
	 * @example
	 * const filter = new NumberFilter('workerId')
	 *   .equals()
	 *   .description('Collaboratore');
	 *
	 * @public
	 */
	public description(desc: string): this {
		this._description = desc;
		return this;
	}

	// =================
	// COSTRUZIONE CONTESTO QUERY
	// =================

	/**
	 * Aggiunge questo filtro al contesto OData se il filtro è attivo
	 *
	 * @param context - Contesto OData da modificare
	 *
	 * @example
	 * const context = {};
	 * filter.value = 'test';
	 * filter._setContext(context);
	 * // context.filter.and ora contiene la definizione del filtro
	 *
	 * @protected
	 */
	protected _setContext(context: ODataContext): void {
		// Verifica se il filtro è attivo
		if (this.value === undefined || this.value === null || this.value === '') {
			return;
		}

		if (
			this._searchOperator === undefined ||
			this._searchOperator === null ||
			this._searchOperator === SearchOperator.NOOP
		) {
			return;
		}

		// Verifica speciale per ArrayFilter (deve essere implementata dalle sottoclassi)
		if (
			this.constructor.name === 'ArrayFilter' &&
			(!this.value || !Array.isArray(this.value) || (this.value as any[]).length === 0)
		) {
			return;
		}

		const propertyName = this._propertyName.replace(/\./g, '/');

		if (!context.filter) {
			context.filter = {};
		}
		if (!context.filter.and) {
			context.filter.and = [];
		}

		context.filter.and.push(this._buildPropertyDef(propertyName));
	}

	/**
	 * Costruisce la definizione della proprietà per la query OData
	 *
	 * @param propertyName - Nome della proprietà
	 * @param operator - Operatore da utilizzare (opzionale, usa quello del filtro se non specificato)
	 * @param value - Valore da utilizzare (opzionale, usa quello del filtro se non specificato)
	 * @returns Definizione del filtro per OData
	 *
	 * @throws {Error} Se si tenta di nidificare operatori complessi
	 *
	 * @protected
	 */
	protected _buildPropertyDef(
		propertyName: string,
		operator: SearchOperatorType | null = null,
		value: any = null,
	): FilterDefinition {
		if (operator === SearchOperator.ANY) {
			throw new Error('Multiple complex operators cannot be nested!');
		}

		operator = operator !== null ? operator : this._searchOperator;
		value = value !== null ? value : this.value;
		const ret: FilterDefinition = {};

		switch (operator) {
			case SearchOperator.CONTAINS:
			case SearchOperator.STARTSWITH:
			case SearchOperator.ENDSWITH:
				propertyName = `tolower(${propertyName})`;
				ret[propertyName] = {};
				ret[propertyName][this._getOperatorValue(operator)] = value;
				break;

			case SearchOperator.BETWEEN:
				ret[propertyName] = {};
				ret[propertyName][this._getOperatorValue(SearchOperator.GREATEREQUAL)] = value[0];
				ret[propertyName][this._getOperatorValue(SearchOperator.LESSEREQUAL)] = value[1];
				break;

			case SearchOperator.NOTBETWEEN:
				ret[propertyName] = {};
				ret[propertyName][this._getOperatorValue(SearchOperator.LESSERTHAN)] = value[0];
				ret[propertyName][this._getOperatorValue(SearchOperator.GREATERTHAN)] = value[1];
				break;

			case SearchOperator.ANY:
				const goDeeper = (obj: any, props: string[], isLast: boolean): any => {
					let currentLevel = obj;
					props.forEach((key, index) => {
						currentLevel[key] = {};
						currentLevel = currentLevel[key];
						if (index === props.length - 1) {
							currentLevel[this._getOperatorValue()] = [];
							if (isLast) {
								currentLevel[this._getOperatorValue()].push(
									this._buildPropertyDef(this._subPropertyName, this._subSearchOperator),
								);
							} else {
								currentLevel[this._getOperatorValue()].push({});
								currentLevel = currentLevel[this._getOperatorValue()][0];
							}
						}
					});
					return currentLevel;
				};

				const nestedProperties: string[][] = [];
				nestedProperties.push(propertyName.split('/'));
				if (this._nestedPropertyNames) {
					this._nestedPropertyNames.forEach((propName) =>
						nestedProperties.push(propName.split('/')),
					);
				}

				let nestedRet = ret;
				nestedProperties.forEach((properties, index) => {
					nestedRet = goDeeper(nestedRet, properties, index === nestedProperties.length - 1);
				});
				break;

			case SearchOperator.ALL:
				ret[propertyName] = {};
				ret[propertyName][this._getOperatorValue()] = [];
				ret[propertyName][this._getOperatorValue()].push(
					this._buildPropertyDef(this._subPropertyName, this._subSearchOperator),
				);
				break;

			case SearchOperator.ISNULL:
				ret[propertyName] = {};
				ret[propertyName][
					value === true
						? this._getOperatorValue(SearchOperator.EQUAL)
						: this._getOperatorValue(SearchOperator.NOTEQUAL)
				] = null;
				break;

			case SearchOperator.ISNOTNULL:
				ret[propertyName] = {};
				ret[propertyName][
					value === true
						? this._getOperatorValue(SearchOperator.NOTEQUAL)
						: this._getOperatorValue(SearchOperator.EQUAL)
				] = null;
				break;

			default:
				ret[propertyName] = {};
				ret[propertyName][this._getOperatorValue(operator)] = value;
				break;
		}

		return ret;
	}

	/**
	 * Converte un operatore simbolico nella sua rappresentazione stringa per OData
	 *
	 * @param operator - Operatore da convertire (opzionale, usa quello del filtro se non specificato)
	 * @returns Rappresentazione stringa dell'operatore
	 *
	 * @throws {TypeError} Se l'operatore non è valido
	 *
	 * @protected
	 */
	protected _getOperatorValue(operator: SearchOperatorType | null = null): string {
		operator = operator !== null ? operator : this._searchOperator;

		switch (operator) {
			case SearchOperator.EQUAL:
				return 'eq';
			case SearchOperator.NOTEQUAL:
				return 'ne';
			case SearchOperator.CONTAINS:
				return 'contains';
			case SearchOperator.STARTSWITH:
				return 'startswith';
			case SearchOperator.ENDSWITH:
				return 'endswith';
			case SearchOperator.GREATERTHAN:
				return 'gt';
			case SearchOperator.GREATEREQUAL:
				return 'ge';
			case SearchOperator.LESSERTHAN:
				return 'lt';
			case SearchOperator.LESSEREQUAL:
				return 'le';
			case SearchOperator.IN:
				return 'in';
			case SearchOperator.ANY:
				return 'any';
			case SearchOperator.ALL:
				return 'all';
			default:
				throw new TypeError('Parameter is not a valid SearchOperator value.');
		}
	}
}
